
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

export interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
  order_index: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export type FAQInsert = {
  question: string;
  answer: string;
  category: string;
  order_index?: number | null;
};

export type FAQUpdate = Partial<FAQInsert>;

export const useFAQs = () => {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFAQs = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("faq_items")
        .select("*")
        .order("order_index", { ascending: true })
        .order("created_at", { ascending: true });

      if (error) {
        throw error;
      }

      setFaqs(data as FAQ[]);
      setError(null);
    } catch (err: any) {
      console.error("Error fetching FAQs:", err);
      setError(err?.message || "Erro ao carregar as FAQs");
    } finally {
      setIsLoading(false);
    }
  };

  const createFAQ = async (data: FAQInsert): Promise<void> => {
    try {
      const { error } = await supabase.from("faq_items").insert(data);
      
      if (error) {
        throw error;
      }
      
      toast.success("FAQ criada com sucesso!");
      await fetchFAQs();
    } catch (err: any) {
      console.error("Error creating FAQ:", err);
      toast.error("Erro ao criar FAQ: " + (err?.message || "Erro desconhecido"));
      throw err;
    }
  };

  const updateFAQ = async (id: string, data: FAQUpdate): Promise<void> => {
    try {
      const { error } = await supabase
        .from("faq_items")
        .update(data)
        .eq("id", id);
      
      if (error) {
        throw error;
      }
      
      toast.success("FAQ atualizada com sucesso!");
      await fetchFAQs();
    } catch (err: any) {
      console.error("Error updating FAQ:", err);
      toast.error("Erro ao atualizar FAQ: " + (err?.message || "Erro desconhecido"));
      throw err;
    }
  };

  const deleteFAQ = async (id: string): Promise<void> => {
    try {
      const { error } = await supabase
        .from("faq_items")
        .delete()
        .eq("id", id);
      
      if (error) {
        throw error;
      }
      
      toast.success("FAQ excluída com sucesso!");
      await fetchFAQs();
    } catch (err: any) {
      console.error("Error deleting FAQ:", err);
      toast.error("Erro ao excluir FAQ: " + (err?.message || "Erro desconhecido"));
      throw err;
    }
  };

  useEffect(() => {
    fetchFAQs();
  }, []);

  const refresh = () => {
    fetchFAQs();
  };

  return {
    faqs,
    isLoading,
    error,
    refresh,
    createFAQ,
    updateFAQ,
    deleteFAQ,
  };
};
